<template>
  <div class="participant-selector">
    <v-container
      v-if="optionAllAsRadio"
      :class="
        `participant-selector__form-options ${
          optionAll === 'selected' ? '' : 'pb-12 mb-6'
        }`
      "
    >
      <v-radio-group v-model="optionAll" hide-details>
        <v-radio label="All current participants" value="all" />
        <v-radio
          label="All current and future participants"
          value="allAndFuture"
        />
        <v-radio label="Select participants" value="selected" />
      </v-radio-group>
    </v-container>
    <v-form
      ref="form"
      :class="`participant-selector__form`"
      v-if="!optionAllAsRadio || (optionAllAsRadio && optionAll === 'selected')"
      @submit.prevent="onSubmit"
    >
      <v-container class="participant-selector__form-search">
        <bh-text-input
          :no-margin="true"
          higher
          placeholder="Search participants"
          inputBackgroundClass="bhWhite"
          roundedClass="rounded"
          @input-text="searchRun"
        />
      </v-container>
      <v-container
        class="participant-selector__form-options"
        v-if="!optionAllAsRadio && allEnabled"
      >
        <v-checkbox
          v-if="allEnabled"
          label="Select all"
          color="accent"
          class="participant-selector__form-option-select-all"
          :hide-details="true"
          @change="changeSelectAll($event)"
          v-model="allSelected"
          off-icon="bhi-ellipse"
          on-icon="bhi-check-circle-filled"
        />
      </v-container>
      <v-container class="participant-selector__form-results pt-0">
        <main-user-table
          class="participant-selector__table"
          :headers="headers"
          :items="computedParticipants"
          :selectItems="practiceUserPatients"
          :items-per-page="queryParams.pageSize"
          :server-items-length="practiceUserPatientsPagination.totalCount || -1"
          :page="queryParams.page"
          :row-component="'participant-selector-table-row'"
          @select-participant="selectParticipant"
          @action:get-data="getParticipants"
          :hide-default-footer="hideDefaultFooter"
        />
      </v-container>
    </v-form>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import MainUserTable from '@/components/tables/MainUserTable'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import RouterMixin from '@/mixins/router'
import { debounce } from '@/helpers/debounce'

export default {
  name: 'ParticipantSelector',
  components: {
    MainUserTable,
    BhTextInput,
  },
  props: {
    initialSelection: {
      type: Array,
      default: () => [],
    },
    initialSelectionProp: {
      type: String,
      default: 'id',
    },
    initialSelectAll: {
      type: Boolean,
      default: false,
    },
    initialSelectAllAndFuture: {
      type: Boolean,
      default: false,
    },
    overrideQueryParams: {
      type: Object,
      default: () => {},
    },
    hideDefaultFooter: {
      type: Boolean,
      default: false,
    },
    allAndFutureEnabled: {
      type: Boolean,
      default: false,
    },
    allEnabled: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [RouterMixin],
  data() {
    return {
      dialog: false,
      headers: [],
      selectedParticipants: [],
      allSelected: false,
      allAndFutureSelected: false,
      optionAll: 'selected',
    }
  },
  mounted() {
    this.queryParams = {
      ...this.queryParams,
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.selectedParticipants = [...this.selectedParticipants]
        this.allSelected = this.initialSelectAll
        this.allAndFutureSelected = this.initialSelectAllAndFuture
        if (this.allAndFutureEnabled && this.allEnabled) {
          this.optionAll = this.initialSelectAllAndFuture
            ? 'allAndFuture'
            : this.initialSelectAll
            ? 'all'
            : 'selected'
        }
        this.dialog = true
        this.getDataAction(
          {
            ...this.queryParams,
            ...this.overrideQueryParams,
            practiceUserId: this.profileId,
          },
          false,
        )
      }
    },
    initialSelectAll: function(val) {
      this.allSelected = val
      this.init()
    },
    initialSelectAllAndFuture: function(val) {
      this.allAndFutureSelected = val
      this.init()
    },
    initialSelection: function(val = []) {
      this.selectedParticipants = [...val]
      this.init()
    },
    optionAll: function(val) {
      switch (val) {
        case 'all':
          this.allSelected = true
          this.allAndFutureSelected = false
          break
        case 'allAndFuture':
          this.allSelected = true
          this.allAndFutureSelected = true
          break
        case 'selected':
          this.allSelected = false
          this.allAndFutureSelected = false
          break
        default:
      }
    },
  },
  computed: {
    ...mapState('practicesStates', [
      'practiceUser',
      'practiceUserPatients',
      'practiceUserPatientsPagination',
    ]),
    ...mapState('dictionariesStates', [
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
    ]),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('userStates', ['profileId']),
    isPermitted() {
      if (
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator &&
        this.practiceUser.profileId != this.profileId
      )
        return true
      else if (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
        this.dictionaryBhUsersRoleIds.Admin
      )
        return true
      else return false
    },
    computedParticipants() {
      let participants = []
      this.practiceUserPatients.forEach(item => {
        let selected = false

        const itemIsSelected = this.selectedParticipants.find(
          selectedParticipant =>
            (selectedParticipant[this.initialSelectionProp] ===
              item[this.initialSelectionProp] &&
              item[this.initialSelectionProp]) ||
            (this.initialSelectionProp === 'participantId' &&
              item.id &&
              item.id === selectedParticipant['participantId']) ||
            (this.initialSelectionProp === 'participantId' &&
              item['participantId'] &&
              item['participantId'] === selectedParticipant.id) ||
            (this.initialSelectionProp === 'participantId' &&
              item['id'] &&
              item['id'] === selectedParticipant.id),
        )
        if (itemIsSelected || (this.allSelected && this.allEnabled))
          selected = true
        participants.push({ selected: selected, ...item })
      })
      return participants
    },
    optionAllAsRadio() {
      return this.allEnabled && this.allAndFutureEnabled
    },
  },
  methods: {
    ...mapActions('practicesStates', ['getPracticeUserPatients']),
    getData(data = {}) {
      if (this.overrideQueryParams) {
        Object.entries(this.overrideQueryParams).forEach(el => {
          data.set(el[0], el[1])
        })
      }
      this.getPracticeUserPatients(data)
    },
    closeDialog() {
      this.dialog = false
      this.editedUser = { practiceId: this.practiceId }
      this.userToSave = {}
      this.$emit('close-dialog')
    },
    clearForm() {
      this.$refs.form.reset()
    },
    onSubmit() {
      this.$emit('on-submit', this.selectedParticipants)
      this.editedUser = { practiceId: this.practiceId }
      this.userToSave = {}
      this.closeDialog()
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    changeSelectAll(value) {
      this.allSelected = value
    },
    searchRun(text) {
      debounce(
        () => {
          this.getDataAction({ ...this.queryParams, fullName: text }, false)
        },
        500,
        'getParticipants',
      )
    },
    selectParticipant(id) {
      const participant = this.computedParticipants.find(el => el.id === id)
      const indexOfSelectedParticipant = this.selectedParticipants.findIndex(
        selectedParticipant =>
          (selectedParticipant[this.initialSelectionProp] ===
            participant[this.initialSelectionProp] &&
            participant[this.initialSelectionProp]) ||
          (this.initialSelectionProp === 'participantId' &&
            participant.id &&
            participant.id === selectedParticipant['participantId']) ||
          (this.initialSelectionProp === 'participantId' &&
            participant['participantId'] &&
            participant['participantId'] === selectedParticipant.id) ||
          (this.initialSelectionProp === 'participantId' &&
            participant['id'] &&
            participant['id'] === selectedParticipant.id),
      )

      if (indexOfSelectedParticipant >= 0) {
        this.selectedParticipants.splice(indexOfSelectedParticipant, 1)
      } else {
        this.selectedParticipants.push(participant)
      }
    },
    getParticipants(data) {
      debounce(
        () => {
          this.getDataAction(
            {
              ...this.queryParams,
              ...data,
              ...{ practiceUserId: this.practiceUser.id },
            },
            false,
          )
        },
        500,
        'getParticipants',
      )
    },
    init() {
      this.selectedParticipants = [...this.$props.initialSelection]
      if (this.allAndFutureEnabled && this.allEnabled) {
        this.optionAll = this.$props.initialSelectAllAndFuture
          ? 'allAndFuture'
          : this.$props.initialSelectAll
          ? 'all'
          : 'selected'
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.participant-selector {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: auto;

  &__form {
    display: flex;
    flex-direction: column;
    flex: 1;

    &-results {
      // overflow: auto;
      flex: 1;
    }

    &-options,
    &-option-select-all {
      margin-top: 0;
      padding-top: 0;
    }

    &-option-select-all {
      margin-left: $gutter * 0.75;

      .v-label,
      .v-icon {
        color: $bh-blue;
      }

      .v-label {
        font-weight: 700;
        font-size: 14px;
        line-height: 20px;
      }
    }
  }
  &-dialog {
    // height: 100%;
    max-height: calc(100% - 32px) !important;
    .v-card {
      // height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: $bh-gray--6;
      &__title,
      &__text:not(.normal-dialog__content) {
        background: $bh-white;
      }
      &__actions {
        position: sticky;
        right: 0;
        bottom: 0;
        width: 0;
        padding: 16px !important;
        margin-left: auto;
      }
      .normal-dialog__content {
        overflow: hidden;
        padding: 0 8px;
      }
    }
    &__apply {
      bottom: 0;
      right: 0;
      position: absolute;
      margin: 0 16px 16px 0;
    }
  }
}
</style>
